/**
 * SINGLE SOURCE OF TRUTH for all application routes.
 *
 * Every route is declared here as a manifest entry:
 *   { path, pageName, Component, layout (default true), guard (optional wrapper) }
 * Redirects are declared in REDIRECTS: { from, to }.
 *
 * App.jsx renders this manifest once, applying LayoutWrapper and guards
 * consistently — never add <Route> elements directly in App.jsx.
 */
import { lazy } from 'react';
import { pagesConfig } from './pages.config';
import AdminRouteGuard from '@/components/AdminRouteGuard';

// ── Lazy-loaded pages (not in pages.config) ─────────────────────────────
const ComplianceGapScanner = lazy(() => import('@/pages/ComplianceGapScanner'));
const AIGovernanceDashboard = lazy(() => import('@/pages/AIGovernanceDashboard'));
const AIModelCouncil = lazy(() => import('@/pages/AIModelCouncil'));
const AIFeedbackAnalytics = lazy(() => import('@/pages/AIFeedbackAnalytics'));
const DocumentGenerator = lazy(() => import('@/pages/DocumentGenerator'));
const ComplaintHandling = lazy(() => import('@/pages/ComplaintHandling'));
const SupplierControls = lazy(() => import('@/pages/SupplierControls'));
const InspectionReadiness = lazy(() => import('@/pages/InspectionReadiness'));
const QMSIntegrationDashboard = lazy(() => import('@/pages/QMSIntegrationDashboard'));
const RegulatoryAuditExport = lazy(() => import('@/pages/RegulatoryAuditExport'));
const CAPAWorkflowBoard = lazy(() => import('@/pages/CAPAWorkflowBoard'));
const AIRegulatorySearch = lazy(() => import('@/pages/AIRegulatorySearch'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const TestSuiteManager = lazy(() => import('@/pages/TestSuiteManager'));
const AIInfrastructure = lazy(() => import('@/pages/AIInfrastructure'));
const QADocumentationSoftware = lazy(() => import('@/pages/QADocumentationSoftware'));
const SOPManagementSystem = lazy(() => import('@/pages/SOPManagementSystem'));
const LabAuditPreparation = lazy(() => import('@/pages/LabAuditPreparation'));
const Blog = lazy(() => import('@/pages/Blog'));
const BlogHiddenCost = lazy(() => import('@/pages/BlogHiddenCost'));
const BlogReduceSOPTime = lazy(() => import('@/pages/BlogReduceSOPTime'));
const AITransparencyStatement = lazy(() => import('@/pages/AITransparencyStatement'));
const WhitePapers = lazy(() => import('@/pages/WhitePapers'));
const WhitePaperAnalyticsDashboard = lazy(() => import('@/pages/WhitePaperAnalyticsDashboard'));
const APQRDashboard = lazy(() => import('@/pages/APQRDashboard'));
const ClientUsagePage = lazy(() => import('@/pages/ClientUsagePage'));
const SecurityMonitorDashboard = lazy(() => import('@/pages/SecurityMonitorDashboard'));
const LIMSIntegrationGuide = lazy(() => import('@/pages/LIMSIntegrationGuide'));
const Solutions = lazy(() => import('@/pages/Solutions'));
const DeterministicAIWorkflow = lazy(() => import('@/pages/DeterministicAIWorkflow'));
const DocumentAnalyzer = lazy(() => import('@/pages/DocumentAnalyzer'));
const AdminContentOptimizer = lazy(() => import('@/pages/AdminContentOptimizer'));
const BlogGxPCompliance = lazy(() => import('@/pages/BlogGxPCompliance'));
const BlogCAPARootCause = lazy(() => import('@/pages/BlogCAPARootCause'));
const BlogIQOQPQGuide = lazy(() => import('@/pages/BlogIQOQPQGuide'));
const DeviationIntake = lazy(() => import('@/pages/DeviationIntake'));
const IQOQGenerator = lazy(() => import('@/pages/IQOQGenerator'));
const RegulatoryRevenueEngine = lazy(() => import('@/pages/RegulatoryRevenueEngine'));
const AuditLogSheetSync = lazy(() => import('@/pages/AuditLogSheetSync'));
const LabPresentationGenerator = lazy(() => import('@/pages/LabPresentationGenerator'));
const ClinicalDiagnosticLabs = lazy(() => import('@/pages/industries/ClinicalDiagnosticLabs'));
const EnvironmentalTestingLabs = lazy(() => import('@/pages/industries/EnvironmentalTestingLabs'));
const CannabisHempLabs = lazy(() => import('@/pages/industries/CannabisHempLabs'));
const PharmaGxPSupport = lazy(() => import('@/pages/industries/PharmaGxPSupport'));
const CAPADocumentation = lazy(() => import('@/pages/solutions/CAPADocumentation'));
const SOPManagement = lazy(() => import('@/pages/solutions/SOPManagement'));
const InspectionReadinessPage = lazy(() => import('@/pages/solutions/InspectionReadinessPage'));
const DeviationDocumentation = lazy(() => import('@/pages/solutions/DeviationDocumentation'));
const ValidationSupport = lazy(() => import('@/pages/solutions/ValidationSupport'));
const AIGovernancePage = lazy(() => import('@/pages/solutions/AIGovernancePage'));
const CLIAGapAudit = lazy(() => import('@/pages/blog/CLIAGapAudit'));
const CAPInspectionReadiness = lazy(() => import('@/pages/blog/CAPInspectionReadiness'));
const LabSOPManagement = lazy(() => import('@/pages/blog/LabSOPManagement'));
const FDA21CFRPart11 = lazy(() => import('@/pages/blog/FDA21CFRPart11'));
const FastestLearnerCompliance = lazy(() => import('@/pages/blog/FastestLearnerCompliance'));
const DevSecOpsLessons = lazy(() => import('@/pages/blog/DevSecOpsLessons'));
const ClientPortal = lazy(() => import('@/pages/ClientPortal'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const ROICalculator = lazy(() => import('@/pages/ROICalculator'));
const ChecklistsGuides = lazy(() => import('@/pages/ChecklistsGuides'));
const SpecializedWorkflows = lazy(() => import('@/pages/SpecializedWorkflows'));
const ValidationIntake = lazy(() => import('@/pages/ValidationIntake'));
const CapacityQA = lazy(() => import('@/pages/CapacityQA'));
const GitHubDashboard = lazy(() => import('@/pages/GitHubDashboard'));
const PipelineDashboard = lazy(() => import('@/pages/PipelineDashboard'));
const MonthlyComplianceSummary = lazy(() => import('@/pages/MonthlyComplianceSummary'));
const GxPDraftingAuditLog = lazy(() => import('@/pages/GxPDraftingAuditLog'));
const GapAssessment = lazy(() => import('@/pages/GapAssessment'));
const HealthScore = lazy(() => import('@/pages/HealthScore'));
const TrustCenter = lazy(() => import('@/pages/TrustCenter'));
const IqOqPqValidation = lazy(() => import('@/pages/resources/IqOqPqValidation'));
const GxpLaboratoryComplianceChecklist = lazy(() => import('@/pages/resources/GxpLaboratoryComplianceChecklist'));
const CliaCapComplianceSoftware = lazy(() => import('@/pages/resources/CliaCapComplianceSoftware'));
const AuditReadinessGuide = lazy(() => import('@/pages/AuditReadinessGuide'));
const AuditDashboard = lazy(() => import('@/pages/AuditDashboard'));
const FunctionHealthDashboard = lazy(() => import('@/pages/FunctionHealthDashboard'));
const LeadScoringDashboard = lazy(() => import('@/pages/LeadScoringDashboard'));
const HeadlineExperiment = lazy(() => import('@/pages/HeadlineExperiment'));
const TestHarness = lazy(() => import('@/pages/TestHarness'));
const QualityRequirementAssessment = lazy(() => import('@/pages/QualityRequirementAssessment'));
const CompareAssessments = lazy(() => import('@/pages/CompareAssessments'));
const AuditEvidenceEngine = lazy(() => import('@/pages/AuditEvidenceEngine'));
const ComplianceCalendar = lazy(() => import('@/pages/ComplianceCalendar'));
const LiveDemo = lazy(() => import('@/pages/LiveDemo'));
const PortalPreview = lazy(() => import('@/pages/PortalPreview'));
const TemplateBuilder = lazy(() => import('@/pages/TemplateBuilder'));
const ComplianceFAQPage = lazy(() => import('@/pages/ComplianceFAQPage'));
const QualityPlatform = lazy(() => import('@/pages/QualityPlatform'));
const AdminHub = lazy(() => import('@/pages/AdminHub'));
const RenewalEngine = lazy(() => import('@/pages/RenewalEngine'));
const WeeklyBriefAdmin = lazy(() => import('@/pages/WeeklyBriefAdmin'));
const AffiliateProgram = lazy(() => import('@/pages/AffiliateProgram'));

// ── Migrated from pages.config.js (2026-07-19, performance) ─────────────
const AIUsageDashboard = lazy(() => import('@/pages/AIUsageDashboard'));
const About = lazy(() => import('@/pages/About'));
const WhatIsLabComplyx = lazy(() => import('@/pages/WhatIsLabComplyx'));
const AdminManual = lazy(() => import('@/pages/AdminManual'));
const AuditLog = lazy(() => import('@/pages/AuditLog'));
const AutomatedValidation = lazy(() => import('@/pages/AutomatedValidation'));
const BatchRecordAnalytics = lazy(() => import('@/pages/BatchRecordAnalytics'));
const ClientManagement = lazy(() => import('@/pages/ClientManagement'));
const ClientOnboarding = lazy(() => import('@/pages/ClientOnboarding'));
const ComplianceAuditTrail = lazy(() => import('@/pages/ComplianceAuditTrail'));
const ComplianceRoadmap = lazy(() => import('@/pages/ComplianceRoadmap'));
const ComplianceToolkit = lazy(() => import('@/pages/ComplianceToolkit'));
const Contact = lazy(() => import('@/pages/Contact'));
const DeviationRemediation = lazy(() => import('@/pages/DeviationRemediation'));
const GxPDraftingPortal = lazy(() => import('@/pages/GxPDraftingPortal'));
const MAUDEDashboard = lazy(() => import('@/pages/MAUDEDashboard'));
const PredictiveCAPAEngine = lazy(() => import('@/pages/PredictiveCAPAEngine'));
const PromptManagement = lazy(() => import('@/pages/PromptManagement'));
const RegulatoryAlerts = lazy(() => import('@/pages/RegulatoryAlerts'));
const RegulatoryChangeCompare = lazy(() => import('@/pages/RegulatoryChangeCompare'));
const RegulatoryChangeReviewQueue = lazy(() => import('@/pages/RegulatoryChangeReviewQueue'));
const RegulatoryChat = lazy(() => import('@/pages/RegulatoryChat'));
const RegulatoryImpactEngine = lazy(() => import('@/pages/RegulatoryImpactEngine'));
const RegulatoryIntelligenceDashboard = lazy(() => import('@/pages/RegulatoryIntelligenceDashboard'));
const RegulatoryGovernanceLibrary = lazy(() => import('@/pages/RegulatoryGovernanceLibrary'));
const RegulatoryMapping = lazy(() => import('@/pages/RegulatoryMapping'));
const SOPGenerator = lazy(() => import('@/pages/SOPGenerator'));
const Terms = lazy(() => import('@/pages/Terms'));
const TrainingGapAnalyzer = lazy(() => import('@/pages/TrainingGapAnalyzer'));
const ValidationWizard = lazy(() => import('@/pages/ValidationWizard'));

// ── Legacy pages.config routes ───────────────────────────────────────────
const { Pages, Layout, mainPage } = pagesConfig;
export const AppLayout = Layout;
export const mainPageKey = mainPage ?? Object.keys(Pages)[0];

// Pages consolidated elsewhere — redirected below, never routed directly
const HIDDEN_LEGACY_PAGES = ["ComplianceDashboard", "CrossProjectDashboard"];

const legacyRoutes = [
  { path: "/", pageName: mainPageKey, Component: Pages[mainPageKey], layout: true },
  ...Object.entries(Pages)
    .filter(([key]) => key !== mainPageKey && !HIDDEN_LEGACY_PAGES.includes(key))
    .map(([key, Component]) => ({ path: `/${key}`, pageName: key, Component, layout: true })),
];

// ── Explicit route manifest ──────────────────────────────────────────────
const manifestRoutes = [
  { path: "/RenewalEngine", pageName: "RenewalEngine", Component: RenewalEngine },
  { path: "/WeeklyBriefAdmin", pageName: "WeeklyBriefAdmin", Component: WeeklyBriefAdmin },
  { path: "/AffiliateProgram", pageName: "AffiliateProgram", Component: AffiliateProgram },
  { path: "/ComplianceGapScanner", pageName: "ComplianceGapScanner", Component: ComplianceGapScanner },
  { path: "/AIGovernanceDashboard", pageName: "AIGovernanceDashboard", Component: AIGovernanceDashboard },
  { path: "/AIModelCouncil", pageName: "AIModelCouncil", Component: AIModelCouncil },
  { path: "/AIFeedbackAnalytics", pageName: "AIFeedbackAnalytics", Component: AIFeedbackAnalytics },
  { path: "/DocumentGenerator", pageName: "DocumentGenerator", Component: DocumentGenerator },
  { path: "/ComplaintHandling", pageName: "ComplaintHandling", Component: ComplaintHandling },
  { path: "/SupplierControls", pageName: "SupplierControls", Component: SupplierControls },
  { path: "/InspectionReadiness", pageName: "InspectionReadiness", Component: InspectionReadiness },
  { path: "/QMSIntegrationDashboard", pageName: "QMSIntegrationDashboard", Component: QMSIntegrationDashboard },
  { path: "/RegulatoryAuditExport", pageName: "RegulatoryAuditExport", Component: RegulatoryAuditExport },
  { path: "/CAPAWorkflowBoard", pageName: "CAPAWorkflowBoard", Component: CAPAWorkflowBoard },
  { path: "/AIRegulatorySearch", pageName: "AIRegulatorySearch", Component: AIRegulatorySearch },
  { path: "/pricing", pageName: "Pricing", Component: Pricing },
  { path: "/TestSuiteManager", pageName: "TestSuiteManager", Component: TestSuiteManager },
  { path: "/AIInfrastructure", pageName: "AIInfrastructure", Component: AIInfrastructure },
  { path: "/qa-documentation-software", pageName: "QADocumentationSoftware", Component: QADocumentationSoftware },
  { path: "/sop-management-system", pageName: "SOPManagementSystem", Component: SOPManagementSystem },
  { path: "/lab-audit-preparation", pageName: "LabAuditPreparation", Component: LabAuditPreparation },
  { path: "/blog", pageName: "Blog", Component: Blog },
  { path: "/blog/hidden-cost-of-qa-documentation", pageName: "Blog", Component: BlogHiddenCost },
  { path: "/blog/reduce-sop-writing-time", pageName: "Blog", Component: BlogReduceSOPTime },
  { path: "/AITransparencyStatement", pageName: "AITransparencyStatement", Component: AITransparencyStatement },
  { path: "/WhitePapers", pageName: "WhitePapers", Component: WhitePapers },
  { path: "/admin/white-paper-analytics", pageName: "WhitePaperAnalyticsDashboard", Component: WhitePaperAnalyticsDashboard },
  { path: "/APQRDashboard", pageName: "APQRDashboard", Component: APQRDashboard },
  { path: "/ClientUsage", pageName: "ClientUsage", Component: ClientUsagePage },
  { path: "/SecurityMonitorDashboard", pageName: "SecurityMonitorDashboard", Component: SecurityMonitorDashboard, guard: AdminRouteGuard },
  { path: "/LIMSIntegrationGuide", pageName: "LIMSIntegrationGuide", Component: LIMSIntegrationGuide },
  { path: "/DeterministicAIWorkflow", pageName: "DeterministicAIWorkflow", Component: DeterministicAIWorkflow },
  { path: "/DocumentAnalyzer", pageName: "DocumentAnalyzer", Component: DocumentAnalyzer },
  { path: "/admin/content-optimizer", pageName: "AdminContentOptimizer", Component: AdminContentOptimizer },
  { path: "/blog/gxp-compliance-guide-small-labs", pageName: "Blog", Component: BlogGxPCompliance },
  { path: "/blog/capa-root-cause-analysis", pageName: "Blog", Component: BlogCAPARootCause },
  { path: "/blog/iq-oq-pq-validation-guide", pageName: "Blog", Component: BlogIQOQPQGuide },
  { path: "/DeviationIntake", pageName: "DeviationIntake", Component: DeviationIntake },
  { path: "/IQOQGenerator", pageName: "IQOQGenerator", Component: IQOQGenerator },
  { path: "/RegulatoryRevenueEngine", pageName: "RegulatoryRevenueEngine", Component: RegulatoryRevenueEngine },
  { path: "/admin/audit-log-sheet-sync", pageName: "AuditLogSheetSync", Component: AuditLogSheetSync },
  { path: "/LabPresentationGenerator", pageName: "LabPresentationGenerator", Component: LabPresentationGenerator },

  // Client Portal
  { path: "/client-portal", pageName: "ClientPortal", Component: ClientPortal },
  { path: "/client-portal/gxp-drafting", pageName: "ClientPortal", Component: ClientPortal },
  { path: "/client-portal/ich-compliance", pageName: "ClientPortal", Component: ClientPortal },
  { path: "/client-portal/specialized-workflows", pageName: "ClientPortal", Component: ClientPortal },

  // Industries
  { path: "/industries/clinical-diagnostic", pageName: "Industries", Component: ClinicalDiagnosticLabs },
  { path: "/industries/environmental-testing", pageName: "Industries", Component: EnvironmentalTestingLabs },
  { path: "/industries/cannabis-hemp", pageName: "Industries", Component: CannabisHempLabs },
  { path: "/industries/pharma-gxp", pageName: "Industries", Component: PharmaGxPSupport },

  // Solutions
  { path: "/solutions", pageName: "Solutions", Component: Solutions },
  { path: "/solutions/capa-documentation", pageName: "Solutions", Component: CAPADocumentation },
  { path: "/solutions/sop-management", pageName: "Solutions", Component: SOPManagement },
  { path: "/solutions/inspection-readiness", pageName: "Solutions", Component: InspectionReadinessPage },
  { path: "/solutions/deviation-documentation", pageName: "Solutions", Component: DeviationDocumentation },
  { path: "/solutions/validation-support", pageName: "Solutions", Component: ValidationSupport },
  { path: "/solutions/ai-governance", pageName: "Solutions", Component: AIGovernancePage },

  // Standalone pages
  { path: "/privacy", pageName: "Privacy", Component: Privacy },
  { path: "/roi-calculator", pageName: "ROICalculator", Component: ROICalculator },
  { path: "/resources/checklists-guides", pageName: "ChecklistsGuides", Component: ChecklistsGuides },
  { path: "/specialized-workflows", pageName: "SpecializedWorkflows", Component: SpecializedWorkflows },
  { path: "/validation-intake", pageName: "ValidationIntake", Component: ValidationIntake },
  { path: "/capacity-qa", pageName: "CapacityQA", Component: CapacityQA },
  { path: "/gap-assessment", pageName: "GapAssessment", Component: GapAssessment },
  { path: "/health-score", pageName: "HealthScore", Component: HealthScore },
  { path: "/trust-center", pageName: "TrustCenter", Component: TrustCenter },
  { path: "/resources/iq-oq-pq-validation", pageName: "IqOqPqValidation", Component: IqOqPqValidation },
  { path: "/resources/gxp-laboratory-compliance-checklist", pageName: "GxpLaboratoryComplianceChecklist", Component: GxpLaboratoryComplianceChecklist },
  { path: "/resources/clia-cap-compliance-software", pageName: "CliaCapComplianceSoftware", Component: CliaCapComplianceSoftware },
  { path: "/resources/audit-readiness-guide", pageName: "AuditReadinessGuide", Component: AuditReadinessGuide },

  // Lowercase nav aliases — the header/footer link to /about and /contact (lowercase),
  // but the only routes were capitalized /About and /Contact. React Router paths are
  // case-sensitive, so those nav links 404'd. Route both cases to the same page.
  { path: "/about", pageName: "About", Component: About },
  { path: "/contact", pageName: "Contact", Component: Contact },
  { path: "/AuditDashboard", pageName: "AuditDashboard", Component: AuditDashboard, guard: AdminRouteGuard },
  { path: "/admin/function-health", pageName: "FunctionHealthDashboard", Component: FunctionHealthDashboard, guard: AdminRouteGuard },
  { path: "/TestHarness", pageName: "TestHarness", Component: TestHarness },
  { path: "/template-builder", pageName: "TemplateBuilder", Component: TemplateBuilder },
  { path: "/governance-faq", pageName: "ComplianceFAQ", Component: ComplianceFAQPage },
  { path: "/platform", pageName: "QualityPlatform", Component: QualityPlatform },

  // Admin
  { path: "/admin", pageName: "AdminHub", Component: AdminHub },
  { path: "/admin/github", pageName: "GitHubDashboard", Component: GitHubDashboard, guard: AdminRouteGuard },
  { path: "/admin/pipeline", pageName: "PipelineDashboard", Component: PipelineDashboard },
  { path: "/admin/monthly-compliance", pageName: "MonthlyComplianceSummary", Component: MonthlyComplianceSummary },
  { path: "/admin/gxp-audit-log", pageName: "GxPDraftingAuditLog", Component: GxPDraftingAuditLog },
  { path: "/admin/lead-scoring", pageName: "LeadScoringDashboard", Component: LeadScoringDashboard },
  { path: "/admin/headline-experiment", pageName: "HeadlineExperiment", Component: HeadlineExperiment, guard: AdminRouteGuard },
  { path: "/admin/quality-assessment", pageName: "QualityRequirementAssessment", Component: QualityRequirementAssessment },
  { path: "/admin/compare-assessments", pageName: "CompareAssessments", Component: CompareAssessments },
  { path: "/admin/evidence-engine", pageName: "AuditEvidenceEngine", Component: AuditEvidenceEngine },
  { path: "/admin/compliance-calendar", pageName: "ComplianceCalendar", Component: ComplianceCalendar },

  // Live demo — public, full-page (no site layout). Un-gated 2026-07-18: was
  // admin-only, silently redirecting the public "Watch the Live Demo" CTA
  // (ComplianceFAQPage.jsx) to /contact instead of showing the demo. Content
  // is entirely static/mocked (no live entity reads, no auth dependency),
  // so opening it carries no data-exposure risk.
  { path: "/demo", pageName: "LiveDemo", Component: LiveDemo, layout: false },

  // Client Portal preview — public, full-page. Static-data mirror of the real
  // Client Portal's tabs (Workspace, Quality Overview, Compliance AI Tools,
  // Implementation Tracker) with a scripted Demo Navigator tour. Added
  // 2026-07-18. Deliberately does not reuse the real portal components—those
  // require auth and fetch live, project-scoped entity data.
  { path: "/portal-preview", pageName: "PortalPreview", Component: PortalPreview, layout: false },

  // Blog pillar posts
  { path: "/blog/clia-gap-audit", pageName: "Blog", Component: CLIAGapAudit },
  { path: "/blog/cap-inspection-readiness", pageName: "Blog", Component: CAPInspectionReadiness },
  { path: "/blog/lab-sop-management", pageName: "Blog", Component: LabSOPManagement },
  { path: "/blog/fda-21-cfr-part-11-compliance-checklist-labs", pageName: "Blog", Component: FDA21CFRPart11 },
  { path: "/blog/fastest-learner-wins-gxp-ai-compliance", pageName: "Blog", Component: FastestLearnerCompliance },
  { path: "/blog/devsecops-lessons-gxp-labs", pageName: "Blog", Component: DevSecOpsLessons },

  // Migrated from pages.config.js (2026-07-19, performance) — same paths as
  // the old auto-derived `/${key}` legacyRoutes convention, now lazy-loaded.
  { path: "/AIUsageDashboard", pageName: "AIUsageDashboard", Component: AIUsageDashboard },
  { path: "/About", pageName: "About", Component: About },
  { path: "/what-is-labcomplyx", pageName: "WhatIsLabComplyx", Component: WhatIsLabComplyx },
  { path: "/AdminManual", pageName: "AdminManual", Component: AdminManual },
  { path: "/AuditLog", pageName: "AuditLog", Component: AuditLog },
  { path: "/AutomatedValidation", pageName: "AutomatedValidation", Component: AutomatedValidation },
  { path: "/BatchRecordAnalytics", pageName: "BatchRecordAnalytics", Component: BatchRecordAnalytics },
  { path: "/ClientManagement", pageName: "ClientManagement", Component: ClientManagement },
  { path: "/ClientOnboarding", pageName: "ClientOnboarding", Component: ClientOnboarding },
  { path: "/ComplianceAuditTrail", pageName: "ComplianceAuditTrail", Component: ComplianceAuditTrail },
  { path: "/ComplianceRoadmap", pageName: "ComplianceRoadmap", Component: ComplianceRoadmap },
  { path: "/ComplianceToolkit", pageName: "ComplianceToolkit", Component: ComplianceToolkit },
  { path: "/Contact", pageName: "Contact", Component: Contact },
  { path: "/DeviationRemediation", pageName: "DeviationRemediation", Component: DeviationRemediation },
  { path: "/GxPDraftingPortal", pageName: "GxPDraftingPortal", Component: GxPDraftingPortal },
  { path: "/MAUDEDashboard", pageName: "MAUDEDashboard", Component: MAUDEDashboard },
  { path: "/PredictiveCAPAEngine", pageName: "PredictiveCAPAEngine", Component: PredictiveCAPAEngine },
  { path: "/PromptManagement", pageName: "PromptManagement", Component: PromptManagement },
  { path: "/RegulatoryAlerts", pageName: "RegulatoryAlerts", Component: RegulatoryAlerts, guard: AdminRouteGuard },
  { path: "/RegulatoryChangeCompare", pageName: "RegulatoryChangeCompare", Component: RegulatoryChangeCompare, guard: AdminRouteGuard },
  { path: "/RegulatoryChangeReviewQueue", pageName: "RegulatoryChangeReviewQueue", Component: RegulatoryChangeReviewQueue, guard: AdminRouteGuard },
  { path: "/RegulatoryChat", pageName: "RegulatoryChat", Component: RegulatoryChat },
  { path: "/RegulatoryImpactEngine", pageName: "RegulatoryImpactEngine", Component: RegulatoryImpactEngine, guard: AdminRouteGuard },
  { path: "/RegulatoryIntelligenceDashboard", pageName: "RegulatoryIntelligenceDashboard", Component: RegulatoryIntelligenceDashboard, guard: AdminRouteGuard },
  { path: "/RegulatoryGovernanceLibrary", pageName: "RegulatoryGovernanceLibrary", Component: RegulatoryGovernanceLibrary, guard: AdminRouteGuard },
  { path: "/RegulatoryMapping", pageName: "RegulatoryMapping", Component: RegulatoryMapping, guard: AdminRouteGuard },
  { path: "/SOPGenerator", pageName: "SOPGenerator", Component: SOPGenerator },
  { path: "/Terms", pageName: "Terms", Component: Terms },
  { path: "/TrainingGapAnalyzer", pageName: "TrainingGapAnalyzer", Component: TrainingGapAnalyzer },
  { path: "/ValidationWizard", pageName: "ValidationWizard", Component: ValidationWizard },
];

// ── Access policy: login required by default ────────────────────────────
// Only the marketing site, lead-capture forms and legal pages are public.
// ClientPortal keeps its own PortalGuard (invitation-only sign-in screen).
const PUBLIC_EXACT = new Set([
  "/", "/pricing", "/about", "/contact", "/solutions", "/privacy", "/terms",
  "/roi-calculator", "/validation-intake", "/health-score", "/gap-assessment",
  "/trust-center", "/governance-faq", "/what-is-labcomplyx", "/qa-documentation-software",
  "/sop-management-system", "/lab-audit-preparation", "/aitransparencystatement",
  "/whitepapers", "/demo", "/portal-preview", "/blog",
  "/client-portal", "/client-portal/gxp-drafting", "/client-portal/ich-compliance",
  "/client-portal/specialized-workflows",
]);
const PUBLIC_PREFIXES = ["/solutions/", "/industries/", "/resources/", "/blog/"];

const isPublicPath = (path) => {
  const p = path.toLowerCase();
  return PUBLIC_EXACT.has(p) || PUBLIC_PREFIXES.some((prefix) => p.startsWith(prefix));
};

export const ROUTES = [...legacyRoutes, ...manifestRoutes].map((route) =>
  route.guard || isPublicPath(route.path) ? route : { ...route, guard: AdminRouteGuard }
);

// ── Redirects (legacy URLs, consolidations, aliases) ─────────────────────
export const REDIRECTS = [
  { from: "/Offerings", to: "/pricing" },
  { from: "/assessment", to: "/health-score" },
  { from: "/ICHComplianceHub", to: "/client-portal?tab=compliance" },
  { from: "/industries", to: "/solutions#lab-types" },
  { from: "/resources", to: "/solutions#resources" },
  { from: "/gap-assessment-intake", to: "/gap-assessment" },
  { from: "/ISO17025AuditCenter", to: "/platform" },
  { from: "/BenchlingSetup", to: "/client-portal?tab=lims" },
  { from: "/ComplianceDashboard", to: "/AuditDashboard" },
  { from: "/CrossProjectDashboard", to: "/AuditDashboard" },
  { from: "/live-demo", to: "/demo" },
  { from: "/RegulatoryIntelligence", to: "/RegulatoryChangeReviewQueue" }, // Regulatory Intelligence Phase 3.B fix (2026-07-17): was pointing to /RegulatoryAlerts, orphaning RegulatoryIntelligence scan data behind a page that only reads RegulatoryChangeAlert. Now points to the governed review queue that actually surfaces both RegulatoryChange and unpromoted RegulatoryIntelligence records.
  { from: "/ClientPortal", to: "/client-portal" },
  { from: "/ValidationIntake", to: "/validation-intake" },
  { from: "/ROICalculator", to: "/roi-calculator" }, // QA audit fix 2026-07-18: was missing from this migration list, causing a live 404 on a page linked from the site's own blog post and SEO config
  { from: "/roicalculator", to: "/roi-calculator" },
  { from: "/Home", to: "/" },
  { from: "/AutomatedComplianceReports", to: "/admin/monthly-compliance" },
  { from: "/GxPSearchEngine", to: "/capacity-qa" },
  { from: "/industries/PharmaGxPSupport", to: "/industries/pharma-gxp" },
  { from: "/LabAuditPreparation", to: "/lab-audit-preparation" },
  { from: "/QADocumentationSoftware", to: "/qa-documentation-software" },
  { from: "/SOPManagementSystem", to: "/sop-management-system" },
  { from: "/TrainingModules", to: "/solutions#resources" },
  { from: "/BlogHiddenCost", to: "/blog/hidden-cost-of-qa-documentation" },
  { from: "/services", to: "/solutions" },

  // Legacy PascalCase blog post routes (pre-slug-migration) — these were indexed by
  // Google before the /blog/* posts moved to friendly slugs, and with no redirect in
  // place they soft-404 (HTTP 200, no noindex), causing Search Console to treat the
  // dead PascalCase URL as a duplicate/canonical over the real live slug post.
  { from: "/blog/CLIAGapAudit", to: "/blog/clia-gap-audit" },
  { from: "/blog/CAPInspectionReadiness", to: "/blog/cap-inspection-readiness" },
  { from: "/blog/LabSOPManagement", to: "/blog/lab-sop-management" },
  { from: "/blog/FDA21CFRPart11", to: "/blog/fda-21-cfr-part-11-compliance-checklist-labs" },
  { from: "/blog/FastestLearnerCompliance", to: "/blog/fastest-learner-wins-gxp-ai-compliance" },
  { from: "/blog/DevSecOpsLessons", to: "/blog/devsecops-lessons-gxp-labs" },
];